import { COLORS } from './Colors';
import { Platform } from 'react-native';

/**
 * Android shadows.
 *
 * `elevation` draws NOTHING on these components under RN 0.86. Android derives an
 * elevation shadow from the view's outline, which comes from its background drawable,
 * and every one of our shadow wrappers is a transparent View wrapping the card -- so
 * there is no outline to cast from. Verified on device: even elevation 24 with a bright
 * red shadowColor rendered no shadow at all, while the same view with boxShadow
 * rendered immediately.
 *
 * boxShadow (RN 0.76+) needs no background, so it is added for Android only. The iOS
 * shadow* props are left exactly as they were -- they still work there, and touching
 * them would change a look that is currently correct.
 *
 * `elevation` is now zeroed on Android wherever a boxShadow is set, because the earlier
 * reasoning stopped being true the moment this helper was added. Elevation needs an
 * OUTLINE, which comes from the background drawable -- and in RN 0.86 both setBorderRadius
 * and setBoxShadow call ensureCompositeBackgroundDrawable, whose getOutline() then
 * supplies exactly the outline that was missing. So on any view that also has a
 * background and a radius (Tooltip's bubble, ConfirmDialog's card, the dashboard tooltip
 * style) elevation would start painting a SECOND shadow on top of the boxShadow.
 *
 * Losing elevation costs nothing for stacking here: under the New Architecture
 * (newArchEnabled=true) zIndex orders Android siblings on its own.
 *
 * NOTE the spread order below: androidShadow(...) goes LAST in every token, so its
 * elevation: 0 wins over the iOS `elevation:` line above it. Spread first and it is
 * silently overridden -- which is what happened when this helper was first added.
 */
const androidShadow = (boxShadow: string) =>
  (Platform.OS === 'android' ? { boxShadow, elevation: 0 } : {});



export const DropShadowLightDistantStyle = {
  shadowColor: '#D8DDEA',
  shadowOffset: { width: 0, height: 20 },
  shadowOpacity: 0.04,
  shadowRadius: 40,
  elevation: 40,
  ...androidShadow('0px 20px 40px rgba(216, 221, 234, 0.04)'),
};

export const DropShadowLightCloseStyle = {
  shadowColor: '#D8DDEA',
  shadowOffset: { width: 0, height: 5 },
  shadowOpacity: 0.02,
  shadowRadius: 5,
  elevation: 5,
  ...androidShadow('0px 5px 5px rgba(216, 221, 234, 0.02)'),
};

export const DropShadowLightBlueStyle = {
  shadowColor: COLORS.brand[10],
  shadowOffset: { width: 0, height: 0 },
  shadowOpacity: 0.5,
  shadowRadius: 12,
  elevation: 15,
  ...androidShadow('0px 0px 12px rgba(233, 232, 250, 0.5)'),
};



export const DropShadowMediumDistantStyle = {
  shadowColor: '#7F8DB9',
  shadowOffset: { width: 0, height: 40 },
  shadowOpacity: 0.08,
  shadowRadius: 80,
  elevation: 80,
  ...androidShadow('0px 40px 80px rgba(127, 141, 185, 0.08)'),
};




export const DropShadowMediumCloseStyle = {
  shadowColor: '#7F8DB9',
  shadowOffset: { width: 0, height: 10 },  // X: 0; Y: 10
  shadowOpacity: 0.03,  // Opacity (08 in hex is ~3%)
  shadowRadius: 10,     // Blur radius
  elevation: 10,
  ...androidShadow('0px 10px 10px rgba(127, 141, 185, 0.03)'),
};

export const DropShadowMediumCloseStyleAndroid = {
  shadowColor: '#7F8DB9',
  shadowOffset: { width: 0, height: 5 },  // X: 0; Y: 10
  shadowOpacity: 0.03,  // Opacity (08 in hex is ~3%)
  shadowRadius: 1,     // Blur radius
  elevation: 10,
  ...androidShadow('0px 10px 10px rgba(127, 141, 185, 0.03), 0px 40px 80px rgba(127, 141, 185, 0.08)'),
};




